import * as d3 from "d3";
import { doSystemDateRetrieval, parseSystemDate } from "common/api";

export function normalizeUnits(valueStr, units, precision) {
    var num = Number(valueStr);
    if (units == "Mbps" && num > 1000) {
      units = "Gbps";
      num /= 1000;
    }
    return num.toFixed(precision) + " " + units;
  }
  
  export const getAlarmsInfo = () => {
    return ifCl.run("show system alarms").then((response) => {
      const [header, ...rows] = response.trim(/\s/).split("\n");
      const headerAttrs = header.split(/\s+/);
      const attrsMap = headerAttrs.reduce((acc, attr, index) => {
        acc[index] = attr.toLowerCase();
        return acc;
      }, {});
  
      return rows.reduce((acc, row) => {
        const [type, ...rowAttrs] = row.split(/\s+/);
        const rowInfo = rowAttrs.reduce((accRow, attr, index) => {
          accRow[attrsMap[index + 1]] = attr;
          return accRow;
        }, {});
        acc[type] = rowInfo;
  
        return acc;
      }, {});
    });
  };
  
  // YYYY-MM-DD HH:mm:ss
  export const timeFormat = d3.timeFormat("%Y-%m-%d %H:%M:%S");
  
  export const getSystemDate = () => {
    return doSystemDateRetrieval().then((response) => {
      const attrs = response.trim(/\s/).split(/\s+/);
      const m = attrs[attrs.length - 1];
      const parseDate = timeFormat(parseSystemDate(response));
      return `${parseDate} ${m}`;
    });
  };
  
  export const getVersion = () => {
    return ifCl.run("show version").then((response) => {
      const valueRelease = response.match(/BQN release: (.*)/);
      if (valueRelease) {
        return valueRelease[1];
      }
      return "unknown";
    });
  };
  
  export const getSystemName = () => {
    return ifCl.run("show system hostname").then((response) => response);
  };
  
  export const getHardwareId = () => {
    return ifCl.run("show hardware id").then((response) => {
      const valueId = response.match(/0x\w+/g);
      if (valueId) {
        return valueId[0];
      }
      return "unknown";
    });
  };
  
  export const getStartTime = () => {
    return ifCl.run("show process name ctrmgr 0").then((response) => {
      const valueTime = response.match(/Time start: (.*)/);
      if (valueTime) {
        return valueTime[1].substring(0, 25);
      }
      return "unknown";
    });
  };
  
  export const getSerialNumber = () => {
    return ifCl.run("show hardware chassis").then((response) => {
      const valueSerial = response.match(/Serial: (.*)/);
      if (valueSerial) {
        return Number(valueSerial[1]) == 0 ? "n/a" : valueSerial[1];
      }
      return "n/a";
    });
  };
  
  export const getLicense = () => {
    return ifCl.run("show license").then((response) => {
      const [_head, ...rows] = response.trim(/\s/).split("\n");
      const license = rows.find((line) => line.indexOf("bta") > -1);
      if (license !== undefined) {
        const [_feature, expiration, _valid, usageMax] = license
          .trim(" ")
          .split(/\s+/);
        return [usageMax, expiration.replace("T", " ")];
      }
      return ["n/a", "n/a"];
    });
  };
  
  export const getSubscriberGroupStatus = () => {
    return ifCl.run("show api subscriber-group status").then((response) => {
      const [activeRow, totalRow] = response.trim(/\s/).split("\n");
      const [_, active] = activeRow.trim(/\s/).split(": ");
      return Number(active);
    });
  };
  
  export const getSubscriberUsage = () => {
    return ifCl.run("show subscriber usage").then((response) => {
      const rows = response.trim().split("\n");
      return rows.reduce((acc, row) => {
        const [key, value] = row.trim().split(/\s+/);
        if (key.startsWith("Active") && value !== undefined) {
          return value;
        }
        return acc;
      }, 0);
    });
  };
  
  export const getLatency = () => {
    return ifCl.run("show pkteng latency").then((response) => {
      const [header, valuesRow] = response.trim(/\s/).split("\n");
      const headerAttrs = header.trim(/\s/).split(/\s+/);
      const { indexLatDown, indexLatUp } = headerAttrs.reduce(
        (acc, attr, index) => {
          if (attr === "RTT-MIN-DN") {
            acc.indexLatDown = index;
          }
          if (attr === "RTT-MIN-UP") {
            acc.indexLatUp = index;
          }
          return acc;
        },
        { indexLatDown: null, indexLatUp: null }
      );
      const values = valuesRow.trim(/\s/).split(/\s+/);
      return {
        up: normalizeUnits(values[indexLatUp], "ms", 1),
        down: normalizeUnits(values[indexLatDown], "ms", 1),
      };
    });
  };
  
  export const getStatisticsPktEng = () => {
    return ifCl.run("show pkteng statistics extended").then((response) => {
      const rows = response.trim(/\s/).split("\n");
      return rows.reduce((acc, row) => {
        const [metric1, value1, metric2, value2] = row.trim("\n").split(/\s+/);
        if (metric1) {
          acc[metric1.slice(0, -1)] = parseFloat(value1);
        }
        if (metric2) {
          acc[metric2.slice(0, -1)] = parseFloat(value2);
        }
        return acc;
      }, {});
    });
  };
  
  export function percentOver(value, total) {
    if (value == 0 || total == 0) {
      return "0 %";
    } else {
      const ratio = value / total;
      return ((ratio > 1.0 ? 1.0 : ratio) * 100).toFixed(1) + " %";
    }
  }
  
  export function getDbTCPOpt(
    tcpDnTx,
    prevTcpDnTx,
    tcpUpTx,
    prevTcpUpTx,
    tcpOptDn,
    prevTcpOptDn,
    tcpOptUp,
    prevTcpOptUp
  ) {
    const tcpTotal = tcpDnTx - prevTcpDnTx + (tcpUpTx - prevTcpUpTx);
    if (tcpTotal > 0) {
      const percentage = (
        ((tcpOptDn - prevTcpOptDn + (tcpOptUp - prevTcpOptUp)) * 100) /
        tcpTotal
      ).toFixed(1);
      return `${percentage} %`;
    }
    return "0 %";
  }
  
  export function getdbPps(value, prevValue, elapsed) {
    let dbPpsValue = "n/a";
    let dbPpsUnit = "Kpps";
    if (value >= prevValue) {
      dbPpsValue = value - prevValue;
      if (elapsed > 0) {
        dbPpsValue = dbPpsValue / elapsed;
      }
      if (dbPpsValue >= 1000000) {
        dbPpsValue = (dbPpsValue / 1000000).toFixed(3);
        dbPpsUnit = "Mpps";
      } else if (dbPpsValue > 0) {
        dbPpsValue = (dbPpsValue / 1000).toFixed(3);
      }
    }
    return { dbPpsValue, dbPpsUnit };
  }
  
  export function formatLargeStrNum(valueStr, precision) {
    return Number(valueStr).toLocaleString("en-US", {
      maximumFractionDigits: precision,
    });
  }